/**
 * Customers domain event definitions — catalog 1.1.0 additions
 * (commerce-platform Phase 1, plan §5 / Step 7, task T02).
 *
 * Producer: `services/customers` (phase Step 5) via its outbox relay. These
 * events cover the D5 customer half — per-org profile lifecycle (create,
 * claim, merge, erase) and cross-org sharing (grant, revoke). All are
 * `auditable: true` (phase §5; the merge event is explicitly "audit-rich").
 *
 * PII MINIMIZATION: payloads carry ids, org references, scope lists, and
 * timestamps — never display names, contact fields, or any value covered by a
 * share scope. The erasure event names the tombstoned profile but cannot
 * contain PII (the profile's PII was anonymized before publication).
 */
import {
  defineEvent,
  asEventRecord,
  optionalString,
  requireString,
  requireStringArray,
  type EventDefinition,
  type EventSchema,
} from './registry';

export interface CustomerProfileCreatedPayload {
  profileId: string;
  orgId: string;
  /** Present only for self-claimed creation paths; cashier/card-created profiles are unclaimed. */
  identityId?: string;
  /** Creator kind, one of the `CustomerProfile.createdBy` enum values: `user | cashier | self | kiosk`. */
  createdBy: string;
}

export interface CustomerProfileClaimedPayload {
  profileId: string;
  orgId: string;
  /** The identity that claimed (was linked to) the profile. */
  identityId: string;
}

export interface CustomerProfileMergedPayload {
  orgId: string;
  keepProfileId: string;
  authorizationId?: string;
  actorType?: string;
  actorId?: string;
  /** Profiles absorbed into `keepProfileId` (each gets status=merged + mergedIntoId). */
  mergedProfileIds: string[];
  /** Resulting linked identity of the kept profile, if the merged set was claimed. */
  identityId?: string;
  /** Explicit profile-to-keep mapping for audit and downstream repair. */
  absorbed?: Array<{ profileId: string; mergedIntoId: string }>;
  /** Correlates this durable merge event with the merge authorization. */
  mergeCorrelationId?: string;
  /** Whether the wallet participant was skipped and requires reconciliation. */
  wallets_merge_skipped?: boolean;
  /** Durable wallet merge state, including the pre-commit pending state. */
  wallets_merge_outcome?: 'pending' | 'retry_pending' | 'completed';
  /** Wallet participant failure detail, present only when the merge was skipped. */
  wallets_merge_failure?: string;
}

export interface CustomerShareGrantedPayload {
  /** The `CustomerSharePolicy` id granting access. */
  policyId: string;
  profileId: string;
  grantorOrgId: string;
  granteeOrgId: string;
  /**
   * Granted scopes, subset of: `basic_profile | contact_info | purchase_history |
   * loyalty_points | wallet_balance | membership_status`.
   */
  scopes: string[];
  /** ISO-8601 expiry; absent means open-ended (still revocable). */
  expiresAt?: string;
}

export interface CustomerShareRevokedPayload {
  policyId: string;
  profileId: string;
  grantorOrgId: string;
  granteeOrgId: string;
  /** ISO-8601 timestamp of the revocation. */
  revokedAt: string;
}

export interface CustomerProfileErasedPayload {
  profileId: string;
  orgId: string;
  /** ISO-8601 timestamp of the erasure (PII anonymized + profile tombstoned). */
  erasedAt: string;
  actorType?: string;
  actorId?: string;
}

const profileCreatedSchema: EventSchema<CustomerProfileCreatedPayload> = {
  parse(data: unknown): CustomerProfileCreatedPayload {
    const record = asEventRecord(data, 'customers:profile-created');
    return {
      profileId: requireString(record.profileId, 'profileId', 'customers:profile-created'),
      orgId: requireString(record.orgId, 'orgId', 'customers:profile-created'),
      identityId: optionalString(record.identityId, 'identityId', 'customers:profile-created'),
      createdBy: requireString(record.createdBy, 'createdBy', 'customers:profile-created'),
    };
  },
};

const profileClaimedSchema: EventSchema<CustomerProfileClaimedPayload> = {
  parse(data: unknown): CustomerProfileClaimedPayload {
    const record = asEventRecord(data, 'customers:profile-claimed');
    return {
      profileId: requireString(record.profileId, 'profileId', 'customers:profile-claimed'),
      orgId: requireString(record.orgId, 'orgId', 'customers:profile-claimed'),
      identityId: requireString(record.identityId, 'identityId', 'customers:profile-claimed'),
    };
  },
};

const profileMergedSchema: EventSchema<CustomerProfileMergedPayload> = {
  parse(data: unknown): CustomerProfileMergedPayload {
    const record = asEventRecord(data, 'customers:profile-merged');
    const absorbed = record.absorbed;
    const walletsMergeSkipped = record.wallets_merge_skipped;
    if (
      walletsMergeSkipped !== undefined &&
      walletsMergeSkipped !== null &&
      typeof walletsMergeSkipped !== 'boolean'
    ) {
      throw new Error('customers:profile-merged: field "wallets_merge_skipped" must be a boolean');
    }
    const walletsMergeOutcome = record.wallets_merge_outcome;
    if (
      walletsMergeOutcome !== undefined &&
      walletsMergeOutcome !== null &&
      walletsMergeOutcome !== 'pending' &&
      walletsMergeOutcome !== 'retry_pending' &&
      walletsMergeOutcome !== 'completed'
    ) {
      throw new Error(
        'customers:profile-merged: field "wallets_merge_outcome" must be pending, retry_pending, or completed',
      );
    }
    if (absorbed !== undefined && !Array.isArray(absorbed)) {
      throw new Error('customers:profile-merged: field "absorbed" must be an array');
    }
    return {
      orgId: requireString(record.orgId, 'orgId', 'customers:profile-merged'),
      keepProfileId: requireString(record.keepProfileId, 'keepProfileId', 'customers:profile-merged'),
      authorizationId: optionalString(
        record.authorizationId,
        'authorizationId',
        'customers:profile-merged',
      ),
      actorType: optionalString(record.actorType, 'actorType', 'customers:profile-merged'),
      actorId: optionalString(record.actorId, 'actorId', 'customers:profile-merged'),
      mergedProfileIds: requireStringArray(record.mergedProfileIds, 'mergedProfileIds', 'customers:profile-merged'),
      identityId: optionalString(record.identityId, 'identityId', 'customers:profile-merged'),
      mergeCorrelationId: optionalString(
        record.mergeCorrelationId,
        'mergeCorrelationId',
        'customers:profile-merged',
      ),
      wallets_merge_skipped:
        walletsMergeSkipped === undefined || walletsMergeSkipped === null
          ? undefined
          : walletsMergeSkipped,
      wallets_merge_outcome:
        walletsMergeOutcome === undefined || walletsMergeOutcome === null
          ? undefined
          : walletsMergeOutcome,
      wallets_merge_failure: optionalString(
        record.wallets_merge_failure,
        'wallets_merge_failure',
        'customers:profile-merged',
      ),
      ...(absorbed === undefined
        ? {}
        : {
            absorbed: absorbed.map((entry, index) => {
              const mapping = asEventRecord(entry, `customers:profile-merged absorbed[${index}]`);
              return {
                profileId: requireString(
                  mapping.profileId,
                  'profileId',
                  'customers:profile-merged absorbed',
                ),
                mergedIntoId: requireString(
                  mapping.mergedIntoId,
                  'mergedIntoId',
                  'customers:profile-merged absorbed',
                ),
              };
            }),
          }),
    };
  },
};

const shareGrantedSchema: EventSchema<CustomerShareGrantedPayload> = {
  parse(data: unknown): CustomerShareGrantedPayload {
    const record = asEventRecord(data, 'customers:share-granted');
    return {
      policyId: requireString(record.policyId, 'policyId', 'customers:share-granted'),
      profileId: requireString(record.profileId, 'profileId', 'customers:share-granted'),
      grantorOrgId: requireString(record.grantorOrgId, 'grantorOrgId', 'customers:share-granted'),
      granteeOrgId: requireString(record.granteeOrgId, 'granteeOrgId', 'customers:share-granted'),
      scopes: requireStringArray(record.scopes, 'scopes', 'customers:share-granted'),
      expiresAt: optionalString(record.expiresAt, 'expiresAt', 'customers:share-granted'),
    };
  },
};

const shareRevokedSchema: EventSchema<CustomerShareRevokedPayload> = {
  parse(data: unknown): CustomerShareRevokedPayload {
    const record = asEventRecord(data, 'customers:share-revoked');
    return {
      policyId: requireString(record.policyId, 'policyId', 'customers:share-revoked'),
      profileId: requireString(record.profileId, 'profileId', 'customers:share-revoked'),
      grantorOrgId: requireString(record.grantorOrgId, 'grantorOrgId', 'customers:share-revoked'),
      granteeOrgId: requireString(record.granteeOrgId, 'granteeOrgId', 'customers:share-revoked'),
      revokedAt: requireString(record.revokedAt, 'revokedAt', 'customers:share-revoked'),
    };
  },
};

const profileErasedSchema: EventSchema<CustomerProfileErasedPayload> = {
  parse(data: unknown): CustomerProfileErasedPayload {
    const record = asEventRecord(data, 'customers:profile-erased');
    return {
      profileId: requireString(record.profileId, 'profileId', 'customers:profile-erased'),
      orgId: requireString(record.orgId, 'orgId', 'customers:profile-erased'),
      erasedAt: requireString(record.erasedAt, 'erasedAt', 'customers:profile-erased'),
      actorType: optionalString(record.actorType, 'actorType', 'customers:profile-erased'),
      actorId: optionalString(record.actorId, 'actorId', 'customers:profile-erased'),
    };
  },
};

export const CUSTOMERS_PROFILE_CREATED_EVENT: EventDefinition<CustomerProfileCreatedPayload> =
  defineEvent<CustomerProfileCreatedPayload>({
    type: 'customers:profile-created',
    version: 1,
    schema: profileCreatedSchema,
    auditable: true,
    description:
      'An org-scoped customer profile was created (identityId may be absent for cashier/card-created unclaimed profiles).',
    since: '1.1.0',
  });

export const CUSTOMERS_PROFILE_CLAIMED_EVENT: EventDefinition<CustomerProfileClaimedPayload> =
  defineEvent<CustomerProfileClaimedPayload>({
    type: 'customers:profile-claimed',
    version: 1,
    schema: profileClaimedSchema,
    auditable: true,
    description:
      'An identity claimed a previously unclaimed customer profile via the email-authorized claim flow (single-use token).',
    since: '1.1.0',
  });

export const CUSTOMERS_PROFILE_MERGED_EVENT: EventDefinition<CustomerProfileMergedPayload> =
  defineEvent<CustomerProfileMergedPayload>({
    type: 'customers:profile-merged',
    version: 1,
    schema: profileMergedSchema,
    auditable: true,
    description:
      'Customer profiles were merged after explicit email authorization from the customer; payload carries the full keep/merged mapping for audit.',
    since: '1.4.0',
  });

export const CUSTOMERS_SHARE_GRANTED_EVENT: EventDefinition<CustomerShareGrantedPayload> =
  defineEvent<CustomerShareGrantedPayload>({
    type: 'customers:share-granted',
    version: 1,
    schema: shareGrantedSchema,
    auditable: true,
    description:
      'A grantor org shared a customer profile with a grantee org under explicit scopes and an optional expiry.',
    since: '1.1.0',
  });

export const CUSTOMERS_SHARE_REVOKED_EVENT: EventDefinition<CustomerShareRevokedPayload> =
  defineEvent<CustomerShareRevokedPayload>({
    type: 'customers:share-revoked',
    version: 1,
    schema: shareRevokedSchema,
    auditable: true,
    description: 'A previously granted customer profile share policy was revoked by the grantor org.',
    since: '1.1.0',
  });

export const CUSTOMERS_PROFILE_ERASED_EVENT: EventDefinition<CustomerProfileErasedPayload> =
  defineEvent<CustomerProfileErasedPayload>({
    type: 'customers:profile-erased',
    version: 1,
    schema: profileErasedSchema,
    auditable: true,
    description:
      'A customer exercised the right to erasure: profile PII was anonymized and the profile tombstoned (transactional/ledger history retained for fiscal integrity).',
    since: '1.1.0',
  });

export const CUSTOMERS_EVENTS: Readonly<Record<string, EventDefinition>> = {
  [CUSTOMERS_PROFILE_CREATED_EVENT.type]: CUSTOMERS_PROFILE_CREATED_EVENT,
  [CUSTOMERS_PROFILE_CLAIMED_EVENT.type]: CUSTOMERS_PROFILE_CLAIMED_EVENT,
  [CUSTOMERS_PROFILE_MERGED_EVENT.type]: CUSTOMERS_PROFILE_MERGED_EVENT,
  [CUSTOMERS_SHARE_GRANTED_EVENT.type]: CUSTOMERS_SHARE_GRANTED_EVENT,
  [CUSTOMERS_SHARE_REVOKED_EVENT.type]: CUSTOMERS_SHARE_REVOKED_EVENT,
  [CUSTOMERS_PROFILE_ERASED_EVENT.type]: CUSTOMERS_PROFILE_ERASED_EVENT,
};
